import { createContext, useContext, useState, ReactNode, useMemo } from "react";

import { useDialogSteps } from "ui/dialog";

import { DictionaryDTO, MondayCampaignTemplateDTO } from "generated/schemas";

import { useInitialData } from "components/modals/create-campaign-from-template-modal/hooks/use-initial-data";
import { CREATE_CAMPAIGN_FROM_TEMPLATE_STEPS } from "components/modals/create-campaign-from-template-modal/create-campaign-from-template-modal.constants";

import type { CampaignFromTemplateContextType } from "components/modals/create-campaign-from-template-modal/create-campaign-from-template-modal.types";

const CampaignFromTemplateContext = createContext<CampaignFromTemplateContextType | undefined>(
  undefined,
);

interface CampaignFromTemplateContextProviderProps {
  children: ReactNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const CampaignFromTemplateContextProvider = ({
  children,
  isOpen,
  setIsOpen,
}: CampaignFromTemplateContextProviderProps) => {
  const steps = useDialogSteps({ total: CREATE_CAMPAIGN_FROM_TEMPLATE_STEPS });
  const [campaignTemplates, setCampaignTemplates] = useState<MondayCampaignTemplateDTO[]>([]);
  const [isLoadingCampaignTemplates, setIsLoadingCampaignTemplates] = useState(false);
  const [campaignObjectives, setCampaignObjectives] = useState<DictionaryDTO[]>([]);
  const [isLoadingCampaignObjectives, setIsLoadingCampaignObjectives] = useState(false);
  const [invalidTemplateIds, setInvalidTemplateIds] = useState<Set<string>>(new Set());
  const [lastFetchedTemplatesParams, setLastFetchedTemplatesParams] = useState<string>("");
  const [lastFetchedObjectivesParams, setLastFetchedObjectivesParams] = useState<string>("");

  const {
    strategies,
    isLoadingStrategies,
    audienceTypes,
    isLoadingAudiences,
    programTypes,
    isLoadingProgramTypes,
  } = useInitialData(isOpen);

  /* `steps`' callbacks use functional updates, so the memo only needs `steps.step`. */
  const value = useMemo(
    () => ({
      ...steps,
      isOpen,
      setIsOpen,
      strategies,
      isLoadingStrategies,
      campaignTemplates,
      setCampaignTemplates,
      isLoadingCampaignTemplates,
      setIsLoadingCampaignTemplates,
      campaignObjectives,
      setCampaignObjectives,
      isLoadingCampaignObjectives,
      setIsLoadingCampaignObjectives,
      audienceTypes,
      isLoadingAudiences,
      programTypes,
      isLoadingProgramTypes,
      invalidTemplateIds,
      setInvalidTemplateIds,
      lastFetchedTemplatesParams,
      setLastFetchedTemplatesParams,
      lastFetchedObjectivesParams,
      setLastFetchedObjectivesParams,
    }),
    [
      isOpen,
      setIsOpen,
      steps.step,
      strategies,
      isLoadingStrategies,
      campaignTemplates,
      isLoadingCampaignTemplates,
      campaignObjectives,
      isLoadingCampaignObjectives,
      audienceTypes,
      isLoadingAudiences,
      programTypes,
      isLoadingProgramTypes,
      invalidTemplateIds,
      lastFetchedTemplatesParams,
      lastFetchedObjectivesParams,
    ],
  );

  return (
    <CampaignFromTemplateContext.Provider value={value}>
      {children}
    </CampaignFromTemplateContext.Provider>
  );
};

export const useCampaignFromTemplateContext = () => {
  const context = useContext(CampaignFromTemplateContext);
  if (context === undefined) {
    throw new Error(
      "useCampaignFromTemplateContext must be used within a CampaignFromTemplateContextProvider",
    );
  }
  return context;
};
